import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { LibraryBrowser } from "@/ui-components/LibraryBrowser";
import { portfolioToLibrarySections } from "@/ui-components/LibraryBrowser/adapters";
import type { LibraryItem, DetailPaneCallbacks } from "@/ui-components/LibraryBrowser";
import { usePortfoliosCRUD } from "../api/usePortfoliosCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds } from "@/shared/api/newsquawk-headlines";
import {
  setPrebuiltOverride,
  clearPrebuiltOverride,
  isPrebuiltWatchlist,
} from "../prebuiltWatchlists";
import {
  clearPrebuiltCombinedColor,
  setPrebuiltCombinedColor,
} from "../prebuiltCombinedOverrides";
import { usePortfolioBuilder } from "./PortfolioBuilderContext";
import ViewAdvancedFilterModal from "../ViewAdvancedFilterModal";
import FilterBuilderBrowser from "./FilterBuilderBrowser";
import WatchlistCreatorBrowser from "./WatchlistCreatorBrowser";
import type { FilterDraft } from "../hooks/useFilterBuilder";
import { BookOpenIcon, PlusIcon } from "@heroicons/react/24/outline";
import type {
  WatchlistCreatorMode,
  SavedWatchlist,
  EditableWatchlist,
} from "../hooks/useWatchlistCreator";
import { formatListName } from "@/shared/utils/formatListName";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { getWatchlistType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import useAuth from "@/hooks/useAuth";

type BuilderView = "build" | "browse";

// Which tab you were last on, for the session only — someone who lives in the
// library shouldn't be routed back to the landing on every visit, but a new
// session should still open on the guided path.
const VIEW_STORAGE_KEY = "nq.watchlists.view";

function initialView(): BuilderView {
  try {
    const stored = sessionStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "build" || stored === "browse") return stored;
  } catch {
    // Private mode / storage disabled — the default is fine.
  }
  return "build";
}

// Static seed from menuCategories
const menuTagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    menuTagIdToName[String(item.id)] = item.title;
  });
});

export default function PortfolioBuilderBrowser() {
  const { portfolios, updateMutation } = usePortfoliosCRUD();
  const { queriesQuery, editMutation: editQueryMutation } = useHeadlineQueriesCRUD();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const {
    setPreviewWatchlist,
    setPreviewFilter,
    setPreviewTagWatchlist,
    isPreviewingWatchlist,
    isPreviewingFilter,
  } = usePortfolioBuilder();
  const { authenticated } = useAuth();

  // Dynamic tag name resolution
  const [resolvedTagNames, setResolvedTagNames] = useState<Record<string, string>>({});

  const tagIdToName = useMemo(
    () => ({ ...menuTagIdToName, ...resolvedTagNames }),
    [resolvedTagNames]
  );

  // Fetch names for tag IDs not in menuCategories
  useEffect(() => {
    const allTagIds = (watchlistsQuery.data || []).flatMap((tw) => tw.tags);
    const unknownIds = allTagIds.filter((id) => !menuTagIdToName[id] && !resolvedTagNames[id]);
    if (unknownIds.length === 0) return;

    const uniqueIds = [...new Set(unknownIds)];
    getTagsByIds(uniqueIds.join(" "))
      .then((tagDetails) => {
        const newNames: Record<string, string> = {};
        if (tagDetails) {
          for (const [tagId, detail] of Object.entries(tagDetails)) {
            const primary = detail.labels?.find((l) => l.primary);
            const name = primary?.name || detail.labels?.[0]?.name;
            if (name) newNames[tagId] = name;
          }
        }
        if (Object.keys(newNames).length > 0) {
          setResolvedTagNames((prev) => ({ ...prev, ...newNames }));
        }
      })
      .catch(() => {});
  }, [watchlistsQuery.data]);

  // Creator state
  const [creatorMode, setCreatorMode] = useState<WatchlistCreatorMode>("ticker");
  const [creatorDraft, setCreatorDraft] = useState<{
    name?: string; tickers?: string[]; keywords?: string[]; tags?: string[];
  } | null>(null);
  const [filterDraft, setFilterDraft] = useState<FilterDraft | null>(null);

  const [view, setViewState] = useState<BuilderView>(initialView);
  const setView = useCallback((next: BuilderView) => {
    setViewState(next);
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Non-fatal — the view just won't be remembered.
    }
  }, []);

  // The concierge panel creates resources itself (one reviewed step); this is
  // only the power-user hand-off, where the filter's watchlists have already
  // been created and resolved to real ids.
  const handleOpenFilterInBuilder = (draft: FilterDraft) => {
    setFilterDraft(draft);
    setEditingFilter(null);
    setAdvancedFilterModalOpen(true);
  };
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [editingTagWatchlist, setEditingTagWatchlist] = useState<TagsWatchlist | null>(null);

  // Advanced filter state
  const [advancedFilterModalOpen, setAdvancedFilterModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<CategoryQuery | null>(null);
  const [viewingFilter, setViewingFilter] = useState<CategoryQuery | null>(null);

  // Deep link: /watchlists-filters?create=ticker|keyword|tag|advanced opens
  // the corresponding creator (used by widget filter panels' "create new"
  // links and the onboarding checklist). Param is consumed and removed.
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Deep link into a combined watchlist, e.g. /watchlists-filters?editFilter=12
   *
   * The filter panel can apply one combined watchlist but cannot build one, so
   * its explanation of that limit needs somewhere to send people. Landing on
   * the page and asking them to find the list again is the kind of half-answer
   * that makes a limit feel like a fault.
   *
   * Consumed once and stripped from the URL, so a refresh or a back-navigation
   * doesn't reopen an editor the user has since closed.
   */
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const wanted = searchParams.get("editFilter");
    if (!wanted) return;
    const filter = (queriesQuery.data || []).find(
      (q) => String(q.id) === wanted
    );
    // Wait for the queries to load rather than giving up on the first render.
    if (!filter) return;
    deepLinkHandled.current = true;
    setView("build");
    setEditingFilter(filter);
    setAdvancedFilterModalOpen(true);
    searchParams.delete("editFilter");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, queriesQuery.data, setView]);

  useEffect(() => {
    const create = searchParams.get("create");
    if (!create) return;
    if (create === "ticker" || create === "keyword" || create === "tag") {
      // The type in this link is legacy; the builder no longer asks for one.
      setCreatorMode(create);
      setEditingPortfolio(null);
      setEditingTagWatchlist(null);
      setView("build");
    } else if (create === "advanced") {
      setEditingFilter(null);
      setAdvancedFilterModalOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume the param once per change
  }, [searchParams]);

  // Build sections from data
  const sections = useMemo(() => {
    const tagWatchlists = watchlistsQuery.data || [];
    const filters = queriesQuery.data || [];

    return portfolioToLibrarySections(portfolios, tagWatchlists, filters, {
      tagIdToName,
      // "Create new..." goes to the builder on this page. There is no type to
      // choose any more, so all three entry points are the same door.
      onCreateTicker: () => {
        setCreatorMode("ticker");
        setEditingPortfolio(null);
        setEditingTagWatchlist(null);
        setView("build");
      },
      onCreateAdvanced: () => {
        setEditingFilter(null);
        setAdvancedFilterModalOpen(true);
      },
    });
  }, [portfolios, watchlistsQuery.data, queriesQuery.data]);

  // Handle item click → preview
  const handleItemClick = useCallback(
    (item: LibraryItem) => {
      const meta = item.metadata ?? {};
      const itemType = meta.itemType as string;

      if (itemType === "ticker" || itemType === "keyword") {
        const portfolioId = meta.portfolioId as number;
        setPreviewWatchlist(portfolioId);
      } else if (itemType === "tag") {
        const tagWatchlistId = meta.tagWatchlistId as number;
        const tags = (meta.tags as { id: string }[]) ?? [];
        setPreviewTagWatchlist(tagWatchlistId, tags.map((t) => t.id));
      } else if (itemType === "advanced-filter") {
        const filterId = meta.filterId as number;
        setPreviewFilter(filterId);
      }
    },
    [setPreviewWatchlist, setPreviewFilter, setPreviewTagWatchlist]
  );

  // Handle item edit — opens the appropriate editor for each item type
  const handleItemEdit = useCallback(
    (item: LibraryItem) => {
      const meta = item.metadata ?? {};
      const itemType = meta.itemType as string;
      const ownerType = meta.ownerType as string;

      if (itemType === "advanced-filter") {
        const filterId = meta.filterId as number;
        const filter = (queriesQuery.data || []).find((q) => q.id === filterId);
        if (filter) {
          if (ownerType === "NEWSQUAWK") {
            setViewingFilter(filter);
          } else {
            setEditingFilter(filter);
            setAdvancedFilterModalOpen(true);
          }
        }
      } else if (itemType === "ticker" || itemType === "keyword") {
        const portfolioId = meta.portfolioId as number;
        const portfolio = portfolios.find((p) => p.id === portfolioId);
        if (portfolio && ownerType === "USER") {
          // Edit in the builder, not the old modal. Opening the dialog put the
          // user in a different editor from the one they created the list in,
          // titled by a type they never chose and fronted by the whole
          // Newsquawk library. Same page, same form, pre-filled.
          const isKeywordOnly = (portfolio.keywords?.length ?? 0) > 0 && portfolio.tickers.length === 0;
          setCreatorMode(isKeywordOnly ? "keyword" : "ticker");
          setEditingTagWatchlist(null);
          setEditingPortfolio(portfolio);
          setView("build");
        }
      } else if (itemType === "tag") {
        const tagWatchlistId = meta.tagWatchlistId as number;
        if (tagWatchlistId && ownerType === "USER") {
          const tagWatchlist = (watchlistsQuery.data ?? []).find((w) => w.id === tagWatchlistId);
          if (tagWatchlist) {
            setCreatorMode("tag");
            setEditingPortfolio(null);
            setEditingTagWatchlist(tagWatchlist);
            setView("build");
          }
        }
      }
    },
    [portfolios, queriesQuery.data, watchlistsQuery.data, setView]
  );

  /**
   * After a save, show the thing that was just made.
   *
   * Saving used to navigate to the library — answering a question the user
   * hadn't asked and hiding the one they had ("did that work?"). Staying on
   * the builder and previewing the saved list puts the answer in the headline
   * strip underneath, and the list itself at the top of "Recently created".
   *
   * The setPreview* calls toggle, so each is skipped when that list is already
   * showing — otherwise saving a list twice in a row turned its preview off.
   */
  const handleSaved = useCallback(
    (saved?: SavedWatchlist) => {
      setEditingPortfolio(null);
      setEditingTagWatchlist(null);
      setCreatorDraft(null);
      // Back to the default form, or the next "Build it myself" after a tag
      // watchlist opened as a tag picker.
      setCreatorMode("ticker");
      if (!saved) return;
      if (saved.kind === "watchlist") {
        if (!isPreviewingWatchlist(saved.id)) setPreviewWatchlist(saved.id);
      } else if (saved.kind === "combined") {
        if (!isPreviewingFilter(saved.id)) setPreviewFilter(saved.id);
      } else {
        // Always set: the tags may have changed even if the list hasn't.
        const tw = (watchlistsQuery.data ?? []).find((w) => w.id === saved.id);
        setPreviewTagWatchlist(saved.id, saved.tags ?? tw?.tags ?? []);
      }
    },
    [
      setPreviewWatchlist,
      setPreviewFilter,
      setPreviewTagWatchlist,
      isPreviewingWatchlist,
      isPreviewingFilter,
      watchlistsQuery.data,
    ]
  );

  /**
   * A library list as the start of one of your own: an editable copy, in the
   * builder that fits its kind, saved only when you save.
   *
   * The same copy the concierge's "Build on it" makes — terms for a
   * watchlist, tags for a tag watchlist, the whole recipe (lists, tags,
   * keyword and every AND/OR) for a combination.
   */
  const handleBuildOn = useCallback(
    (item: EditableWatchlist) => {
      setEditingPortfolio(null);
      setEditingTagWatchlist(null);
      if (item.kind === "watchlist") {
        const p = item.portfolio;
        setCreatorMode(p.tickers?.length ? "ticker" : "keyword");
        setCreatorDraft({
          name: formatListName(p.name),
          tickers: p.tickers,
          keywords: p.keywords ?? [],
        });
      } else if (item.kind === "tag") {
        setCreatorMode("tag");
        setCreatorDraft({
          name: formatListName(item.watchlist.name),
          tags: item.watchlist.tags,
        });
      } else {
        const q = item.query;
        setFilterDraft({
          name: formatListName(q.queryName),
          searchKeyword: q.searchKeyword || undefined,
          tags: q.categories,
          portfolioIds: q.portfolioIds ?? [],
          tagsWatchlistIds: q.tagsWatchlistIds ?? [],
          filterMode: q.filterMode,
          keywordOp: q.keywordOp,
          keywordWatchlistOp: q.keywordWatchlistOp,
        });
        setEditingFilter(null);
        setAdvancedFilterModalOpen(true);
      }
    },
    []
  );

  // Detail pane callbacks
  const detailCallbacks = useMemo<DetailPaneCallbacks>(() => ({
    onEditColor: (item, color) => {
      const meta = item.metadata ?? {};

      // Combined watchlist: stored on the server for the user's own, locally
      // for a Newsquawk one — the same split watchlists use below.
      if (meta.itemType === "advanced-filter") {
        const filterId = meta.filterId as number | undefined;
        const filter = (queriesQuery.data ?? []).find((q) => q.id === filterId);
        if (!filter || filterId === undefined) return;
        if (filter.owner_type === "NEWSQUAWK") {
          setPrebuiltCombinedColor(filterId, color);
        } else {
          editQueryMutation.mutate({ ...filter, color });
        }
        return;
      }

      const portfolioId = meta.portfolioId as number | undefined;
      if (!portfolioId) return;

      const portfolio = portfolios.find((p) => p.id === portfolioId);
      if (!portfolio) return;

      if (isPrebuiltWatchlist(portfolio)) {
        setPrebuiltOverride(portfolioId, { color });
      } else {
        updateMutation.mutate({ ...portfolio, color });
      }
    },
    onEditPriority: (item, priority) => {
      const meta = item.metadata ?? {};
      const portfolioId = meta.portfolioId as number | undefined;
      if (!portfolioId) return;

      const portfolio = portfolios.find((p) => p.id === portfolioId);
      if (!portfolio) return;

      if (isPrebuiltWatchlist(portfolio)) {
        setPrebuiltOverride(portfolioId, { priority });
      } else {
        updateMutation.mutate({ ...portfolio, priority });
      }
    },
    onEdit: (item) => handleItemEdit(item),
    onResetDefaults: (item) => {
      const meta = item.metadata ?? {};
      if (meta.itemType === "advanced-filter") {
        const filterId = meta.filterId as number | undefined;
        if (filterId !== undefined) clearPrebuiltCombinedColor(filterId);
        return;
      }
      const portfolioId = meta.portfolioId as number | undefined;
      if (portfolioId) {
        clearPrebuiltOverride(portfolioId);
      }
    },
  }), [portfolios, updateMutation, handleItemEdit, queriesQuery.data, editQueryMutation]);

  // The page IS the builder. Browsing the library is one click away rather
  // than a peer tab, and the concierge sits inside the builder's own header
  // where its alternative to typing lists by hand actually reads as one.
  const builderHeader = (
    <div className="flex items-center gap-3 border-b border-gray-700/50 bg-gray-800 px-4 py-2.5">
      <h2 className="flex-1 text-sm font-semibold text-gray-100">
        Build a watchlist
      </h2>
      <button
        type="button"
        onClick={() => setView("browse")}
        className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded border border-gray-700 bg-gray-900 px-2.5 py-1 text-xs font-medium text-gray-300 transition-colors hover:border-gray-600 hover:text-white"
      >
        <BookOpenIcon className="size-3.5" />
        Browse library
      </button>
    </div>
  );

  const browseHeader = (
    <div className="flex items-center gap-3 border-b border-gray-700/50 bg-gray-800 px-4 py-2.5">
      <h2 className="flex-1 text-sm font-semibold uppercase tracking-wider text-gray-100">
        Library
      </h2>
      <button
        type="button"
        onClick={() => setView("build")}
        className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded border border-squawk-blue bg-squawk-blue/15 px-2.5 py-1 text-xs font-semibold text-squawk-blue transition-colors hover:bg-squawk-blue/25"
      >
        <PlusIcon className="size-3.5" />
        Build a watchlist
      </button>
    </div>
  );

  return (
    <>
      {view === "build" ? (
        <WatchlistCreatorBrowser
          inline
          isOpen
          onClose={() => setView("browse")}
          mode={creatorMode}
          editingPortfolio={editingPortfolio}
          editingTagWatchlist={editingTagWatchlist}
          draft={creatorDraft}
          headerSlot={builderHeader}
          onOpenInBuilder={handleOpenFilterInBuilder}
          onOpenWatchlist={(draft) => {
            setEditingPortfolio(null);
            setEditingTagWatchlist(null);
            setCreatorDraft(draft);
            setView("build");
          }}
          onSaved={handleSaved}
          onCancelled={() => {
            setEditingPortfolio(null);
            setEditingTagWatchlist(null);
            setCreatorDraft(null);
            setCreatorMode("ticker");
          }}
          onBuildOn={handleBuildOn}
          onEditWatchlist={(item) => {
            // The rail lists all three kinds now, so the click routes to the
            // editor that can actually open the record.
            if (item.kind === "watchlist") {
              setEditingTagWatchlist(null);
              setEditingPortfolio(item.portfolio);
              setCreatorMode(item.portfolio.tickers?.length ? "ticker" : "keyword");
            } else if (item.kind === "tag") {
              setEditingPortfolio(null);
              setEditingTagWatchlist(item.watchlist);
              setCreatorMode("tag");
            } else {
              // A combination carries operators the flat form cannot express,
              // so it opens in the filter builder instead.
              setEditingFilter(item.query);
              setAdvancedFilterModalOpen(true);
            }
          }}
        />
      ) : (
        <LibraryBrowser
          mode="browse"
          sections={sections}
          onPreview={handleItemClick}
          onItemEdit={handleItemEdit}
          detailCallbacks={detailCallbacks}
          header={browseHeader}
          className="flex-[2] min-h-0"
        />
      )}

      <FilterBuilderBrowser
        isOpen={advancedFilterModalOpen}
        onClose={() => { setAdvancedFilterModalOpen(false); setEditingFilter(null); setFilterDraft(null); }}
        queryId={editingFilter?.id}
        draft={filterDraft}
      />

      <ViewAdvancedFilterModal
        isOpen={!!viewingFilter}
        onClose={() => setViewingFilter(null)}
        filter={viewingFilter}
      />
    </>
  );
}
